import React, { useContext } from "react";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress
} from "@mui/material";
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { theme } from "../../theme";
import { type Brand } from "@/logic/api";
import { getFileUrl } from "@/utility/utils";
import { SessionContext } from "@/utility/useSession";

interface BrandListItemProps {
    brand: Brand;
    isDefault: boolean;
    onEdit: (brand: Brand) => void;
    onDelete: (brand: Brand) => void;
    onSetDefault: (brand: Brand) => void;
    disabled?: boolean;
    isSettingDefault?: boolean;
}

export const BrandListItem: React.FC<BrandListItemProps> = ({
    brand,
    isDefault,
    onEdit,
    onDelete,
    onSetDefault,
    disabled = false,
    isSettingDefault = false
}) => {
    const { t } = useTranslation();
    const session = useContext(SessionContext);

    const getLogoUrl = () => {
        if (brand.fileName) {
            return getFileUrl(session, brand.fileName);
        }
        return undefined;
    };

    const logoUrl = getLogoUrl();

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "white",
                transition: "all 0.2s ease",
                "&:hover": {
                    // TODO: DD - make these shadows the same across the app
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)"
                },
            }}
            data-testid={`brand-item-${brand.id}`}
        >
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f8f9fa",
                    mr: 2,
                    flexShrink: 0
                }}
            >
                {logoUrl ? (
                    <Box
                        component="img"
                        src={logoUrl}
                        alt={`${brand.displayName} logo`}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 1
                        }}
                        onError={(e) => {
                            // if for some reason the image fails to load, hide it and show placeholder
                            (e.target as HTMLImageElement).style.display = "none";
                        }}
                    />
                ) : (
                    <ImageIcon sx={{ fontSize: 32, color: "#ccc" }} />
                )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                >
                    {brand.displayName}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                >
                    {t("brand.fields.brandCodeLabel")} {brand.brandCode}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    transition: "opacity 0.2s ease"
                }}
            >
                <Tooltip title={isDefault ? t("brand.actions.removeDefault") : t("brand.actions.setDefault")}>
                    <IconButton
                        onClick={() => onSetDefault(brand)}
                        disabled={disabled || isSettingDefault}
                        size="small"
                        sx={{
                            color: isDefault ? theme.palette.warning.main : "#ccc",
                            "&:hover": {
                                color: theme.palette.warning.main,
                                bgcolor: "rgba(255, 193, 7, 0.1)"
                            }
                        }}
                        data-testid={`set-default-brand-${brand.id}`}
                    >
                        {isSettingDefault ? (
                            <CircularProgress size={18} sx={{ color: theme.palette.warning.main }} />
                        ) : (
                            isDefault ? <StarIcon /> : <StarBorderIcon />
                        )}
                    </IconButton>
                </Tooltip>

                <Tooltip title={t("brand.actions.editBrand")}>
                    <IconButton
                        onClick={() => onEdit(brand)}
                        disabled={disabled}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            "&:hover": {
                                color: theme.palette.primary.main,
                                bgcolor: "rgba(63, 81, 181, 0.1)"
                            }
                        }}
                        data-testid={`edit-brand-${brand.id}`}
                    >
                        <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title={t("brand.actions.deleteBrand")}>
                    <IconButton
                        onClick={() => onDelete(brand)}
                        disabled={disabled}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            "&:hover": {
                                color: "#ef4444",
                                bgcolor: "rgba(239, 68, 68, 0.1)"
                            },
                            "&:disabled": {
                                color: "#ccc"
                            }
                        }}
                        data-testid={`delete-brand-${brand.id}`}
                    >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};